import { isMobile } from '../../utils/check-media'
import { wildcardToRegExp } from './utils'

type PageCondition = 'all' | 'target' | 'tags'
type WhenShowCondition = 'immediately' | 'on_conditions'
type TimeDelay = 'none' | '3sec' | '5sec' | '15sec' | '30sec' | '1min' | '2min'
type PageScroll = '25' | '50' | '75' | '100'
type Frequency = 'once' | '1h' | '12h' | '1d' | '3d' | '1w' | '2w' | 'unlimited'

const timeDelayToMs = (delay: TimeDelay): number => {
  switch (delay) {
    case 'none':
      return 0
    case '3sec':
      return 3000
    case '5sec':
      return 5000
    case '15sec':
      return 15000
    case '30sec':
      return 30_000
    case '1min':
      return 60_000
    case '2min':
      return 120_000
  }
}

const frequencyToMs = (delay: Frequency): number => {
  switch (delay) {
    case '1h':
      return 60 * 60 * 1000
    case '12h':
      return 12 * 60 * 60 * 1000
    case '1d':
      return 24 * 60 * 60 * 1000
    case '3d':
      return 3 * 24 * 60 * 60 * 1000
    case '1w':
      return 7 * 24 * 60 * 60 * 1000
    case '2w':
      return 14 * 24 * 60 * 60 * 1000
    default:
      return 0
  }
}

const SCROLL_THRESHOLD: Record<PageScroll, number> = {
  '25': 20,
  '50': 40,
  '75': 55,
  '100': 99,
}

const clearTag = (str: string) => str.toLowerCase().replace(/\s+/g, '')

export class ConditionRuntime {
  whenShow: WhenShowCondition
  pageCondition: PageCondition
  pages: string | null
  timeDelay: TimeDelay | null
  pageScroll: PageScroll | null
  exitIntent: boolean
  frequency: Frequency
  lastShowDate: Date | null
  productTags: string[]

  private readonly showCallback: () => void
  private startDate: Date
  private timerRef: number

  constructor(
    dataset: Record<string, any>,
    lastShowDate: Date | null,
    show: () => void,
  ) {
    this.showCallback = show
    this.pageCondition = dataset.pageCondition ?? 'all'
    this.whenShow = dataset.whenShow ?? 'immediately'
    this.pages = dataset.pages
    this.timeDelay = dataset.timeDelay === 'none' ? null : dataset.timeDelay
    this.pageScroll = dataset.pageScroll === 'none' ? null : dataset.pageScroll
    this.exitIntent = dataset.exitIntent === 'true' ?? false
    this.frequency = dataset.frequency ?? 'unlimited'
    this.productTags = ((dataset.productTags as string) || '')
      .split(',')
      .map(clearTag)
      .filter(str => str.length > 0)
    this.lastShowDate = lastShowDate
  }

  private readonly _shouldShowOnThisPage = (): boolean => {
    if (this.pageCondition === 'target') {
      const urls =
        this.pages
          ?.split('\n')
          .map(sourceUrl => {
            try {
              return new URL(sourceUrl)
            } catch (e) {
              return false
            }
          })
          .filter(Boolean)
          .map(url => (url as URL).pathname) ?? []

      for (let i = 0; i < urls.length; i++) {
        const wildcard = urls[i]
        if (wildcardToRegExp(wildcard).test(window.location.pathname)) {
          return true
        }
      }

      return false
    }

    if (this.pageCondition === 'tags') {
      const currentProductTags = window.auroraProductTags

      if (
        !currentProductTags ||
        !Array.isArray(currentProductTags) ||
        currentProductTags.length === 0
      ) {
        return false
      }

      return currentProductTags
        .map(clearTag)
        .some(tag => this.productTags.includes(tag))
    }

    return true
  }

  run = (): void => {
    if (!this._shouldShowOnThisPage()) {
      return
    }

    if (this.frequency === 'once' && this.lastShowDate) {
      return
    }

    if (
      this.frequency !== 'unlimited' &&
      this.lastShowDate &&
      Date.now() - frequencyToMs(this.frequency) < this.lastShowDate.getTime()
    ) {
      return
    }

    this.startDate = new Date()
    this.timerRef = window.setInterval(() => {
      this._tick()
    }, 1000)
    this._tick()

    if (this.exitIntent && !isMobile()) {
      document.addEventListener('mouseout', this._handleMouseLeave)
    }

    if (this.pageScroll) {
      window.addEventListener('scroll', this._handleWindowScroll)
      this._handleWindowScroll()
    }
  }

  private readonly _tick = (): void => {
    if (this.whenShow === 'immediately') {
      this._show()
      return
    }

    if (this.timeDelay) {
      if (
        Date.now() - timeDelayToMs(this.timeDelay) >=
        this.startDate.getTime()
      ) {
        this._show()
      }
    }
  }

  private readonly _handleWindowScroll = (): void => {
    const scrollTop = window.scrollY
    const docHeight = document.body.offsetHeight
    const winHeight = window.innerHeight
    const scrollPercent =
      docHeight === winHeight ? 1 : scrollTop / (docHeight - winHeight)
    const scrollPercentRounded = Math.round(scrollPercent * 100)

    if (scrollPercentRounded >= SCROLL_THRESHOLD[this.pageScroll]) {
      this._show()
    }
  }

  private readonly _handleMouseLeave = (e: any): void => {
    if (!e.toElement && !e.relatedTarget) {
      this._show()
    }
  }

  private readonly _show = (): void => {
    this.showCallback()
    this.cleanup()
  }

  cleanup = (): void => {
    clearInterval(this.timerRef)

    if (this.pageScroll) {
      window.removeEventListener('scroll', this._handleWindowScroll)
    }

    if (this.exitIntent && !isMobile()) {
      document.removeEventListener('mouseout', this._handleMouseLeave)
    }
  }
}
